import { Box, Stack, Typography } from '@mui/material';
import { Point } from 'highcharts';
import { get, map, nth } from 'lodash';
import numeral from 'numeral';
import { useMemo, useState } from 'react';
import { Sliders } from 'react-feather';
import { Link } from 'react-router-dom';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import FlagCountry from 'src/components/common/FlagCountry';
import MainContainer from 'src/components/common/MainContainer';
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import { SectionErrorBoundary } from 'src/components/ErrorBoundary/SectionErrorBoundary';
import WorldMapChart from 'src/components/HighChart/WorldMapChart';
import { WorldMapOptions } from 'src/components/HighChart/WorldMapChart/config';
import CustomTable from 'src/components/Table';
import PopupFilterVessels from 'src/features/Vessels/PopupFilter';
import { useQuerySummaryVessels, useQueryVessels } from 'src/services/queries/useQueryVessel';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { formatUTCTime } from 'src/utils/helpers/dayjs';
import { renderTextOrDefault } from 'src/utils/helpers/stringHelpers';

const ListVessels = () => {
  const [params, setParams] = useState({
    limit: 10,
    offset: 0,
  });
  const [visibleFilter, setVisibleFilter] = useState(false);
  const {
    data: { data, total_records, ship_types, operators, owners, shared_line_services },
    isFetching,
  } = useQueryVessels(params);

  const { data: summaryVessels } = useQuerySummaryVessels();

  const configColumns = [
    {
      id: 'MMSI',
      label: 'MMSI',
      width: 100,
      cell: (data: TypeVessel) => (
        <Typography variant="body2" color={'text.secondary'}>
          {renderTextOrDefault(data.mmsi)}
        </Typography>
      ),
    },
    {
      id: 'vessel_name',
      label: 'Vessel Name',
      width: 250,
      cell: (data: TypeVessel) => (
        <Link
          to={{
            pathname: AuthRouteKeys.VesselDetail.replace(':id', String(data.mmsi || '')),
          }}
        >
          <Typography display={'flex'} gap={1} variant="body2" color={'secondary.main'}>
            <FlagCountry code={data.country ?? ''} /> {renderTextOrDefault(data.name)}
          </Typography>
        </Link>
      ),
    },
    {
      id: 'imo',
      label: 'IMO',
      width: 100,
      cell: (data: TypeVessel) => (
        <Typography variant="body2" color={'text.secondary'}>
          {renderTextOrDefault(data.imo)}
        </Typography>
      ),
    },
    {
      id: 'type',
      label: 'Type',
      width: 100,
      cell: (data: TypeVessel) => (
        <Typography variant="body2" color={'text.secondary'}>
          {renderTextOrDefault(data.ship_type)}
        </Typography>
      ),
    },
    {
      id: 'TEU',
      label: 'TEU',
      width: 100,
      cell: (data: TypeVessel) => (
        <Typography variant="body2" color={'text.secondary'}>
          {renderTextOrDefault(data.teu)}
        </Typography>
      ),
    },
    {
      id: 'length',
      label: 'Length',
      width: 100,
      cell: (data: TypeVessel) => (
        <Typography variant="body2" color={'text.secondary'}>
          {renderTextOrDefault(data.length)}
        </Typography>
      ),
    },
    {
      id: 'beam',
      label: 'Beam',
      width: 100,
      cell: (data: TypeVessel) => (
        <Typography variant="body2" color={'text.secondary'}>
          {renderTextOrDefault(data.beam)}
        </Typography>
      ),
    },
    {
      id: 'operator',
      label: 'Operator',
      width: 250,
      cell: (data: TypeVessel) => (
        <Typography variant="body2" color={'text.secondary'}>
          {renderTextOrDefault(data.operator)}
        </Typography>
      ),
    },
    {
      id: 'destination',
      label: 'Destination',
      width: 150,
      cell: (data: TypeVessel) => (
        <Typography display={'flex'} gap={1} variant="body2" color={'text.secondary'}>
          <FlagCountry code={data.country ?? ''} /> {renderTextOrDefault(data.last_destination)}
        </Typography>
      ),
    },
  ] as ConfigCell<TypeVessel>[];

  const points = useMemo(
    () =>
      map(data, (vessel) => {
        const coord = vessel.coord?.split(',');
        const lat = Number(nth(coord, 0));
        const lon = Number(nth(coord, 1));
        return {
          lon: lon,
          lat: lat,
          name: vessel.name,
          teu: vessel.teu,
          length: vessel.length,
          mmsi: vessel.mmsi,
          beam: vessel.beam,
          operator: vessel.operator,
          ship_type: vessel.ship_type,
          last_seen: vessel.last_seen,
          percent_load: vessel.percent_load,
          speed: vessel.speed,
          last_eta: vessel.last_eta,
        };
      }),
    [data],
  );

  const worldMapData = useMemo(
    () => (
      <WorldMapChart
        tooltipFormatter={function () {
          const point: Point = this.point;
          const speedInKMH = ((get(point.options, 'speed') ?? 0) * 1.852).toFixed(2);
          const getValueFromPoint = (key: string) => get(point.options, key);
          return `<div style="display: flex; flex-direction: column">
      <span style="font-weight: 600; font-size: 16px">${getValueFromPoint('name')}</span>
      <span>TEU: ${getValueFromPoint('teu')}</span>
      <span>Length: ${getValueFromPoint('length')}</span>
      <span>MMSI: ${getValueFromPoint('mmsi')}</span>
      <span>Beam: ${getValueFromPoint('beam')}</span>
      <span>Type: ${getValueFromPoint('ship_type')}</span>
      <span>Operator: ${getValueFromPoint('operator')}</span>
      <span>Load: ${getValueFromPoint('percent_load')}%</span>
      <span>Speed: ${getValueFromPoint('speed')}kn (${speedInKMH} km/h)</span>
      <span>Last Seen: ${formatUTCTime(
        FORMAT_DATE_TIME.DD_MM_YYYY_HH_MM_A,
        getValueFromPoint('last_seen'),
        FORMAT_DATE_TIME.UTC_YEAR_FIRST,
      )}</span>
      <span>ETA: ${formatUTCTime(
        FORMAT_DATE_TIME.DD_MM_YYYY_HH_MM_A,
        getValueFromPoint('last_eta'),
        FORMAT_DATE_TIME.UTC_YEAR_FIRST,
      )}</span>
    </div>`;
        }}
        dataPoint={points}
        height={580}
        type={WorldMapOptions.ctd_ports}
        data={[]}
        isLoading={isFetching}
      />
    ),
    [points, isFetching],
  );

  return (
    <MainContainer>
      <PopupFilterVessels
        shipTypes={ship_types}
        operators={operators}
        owners={owners}
        sharedLineServices={shared_line_services}
        open={visibleFilter}
        handleClose={() => setVisibleFilter(false)}
        onFilter={(payload) => setParams({ ...params, ...payload })}
      />
      <WhiteBoxContent>
        <Stack mb={1} justifyContent={'space-between'} direction={'row'}>
          <Typography variant="h5">Global fleet of container vessels</Typography>
          <ButtonWithLoading
            disabled={isFetching}
            isLoading={isFetching}
            variant="outlined"
            onClick={() => setVisibleFilter(true)}
            startIcon={<Sliders size={18} />}
          >
            Filter
          </ButtonWithLoading>
        </Stack>
        <Typography display={'inline'} variant="body2" color={'text.secondary'}>
          Container vessels:{' '}
          <Typography display={'inline'} variant="subtitle1" color={'primary.main'}>
            {numeral(summaryVessels?.data?.total_count).format('0,0')}
          </Typography>
        </Typography>
        <Typography ml={5} display={'inline'} variant="body2" color={'text.secondary'}>
          Total capacity:{' '}
          <Typography display={'inline'} variant="subtitle1" color={'primary.main'}>
            {numeral(summaryVessels?.data?.total_teu).format('0.0a').toUpperCase()} TEU
          </Typography>
        </Typography>

        <SectionErrorBoundary resetKey="VesselWorldMapData" sectionName="VesselWorldMapData">
          <Box my={3}>{worldMapData}</Box>
        </SectionErrorBoundary>

        <SectionErrorBoundary resetKey="VesselTableData" sectionName="VesselTableData">
          <CustomTable
            isHover={false}
            tableMaxHeight={1000}
            onChangePage={(e) => setParams({ ...params, offset: e })}
            onChangePageSize={(e) => setParams({ ...params, offset: 0, limit: e })}
            loading={isFetching}
            totalRecords={total_records}
            page={params.offset}
            pageSize={params.limit}
            data={getArrayDefault(data)}
            dataKey={'id'}
            configColumn={configColumns}
          />
        </SectionErrorBoundary>
      </WhiteBoxContent>
    </MainContainer>
  );
};

export default ListVessels;
